import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;
  // start
  #handlePageShow = (event) => {
    console.log('[bundle-restore] pageshow fired, persisted:', event.persisted);
    if (event.persisted) {
      this.#restoreCartIfNeeded();
    }
  };
  // end

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    // start
    window.addEventListener('pageshow', this.#handlePageShow);
    // end

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
    // start
    this.#restoreCartIfNeeded();
    // end
  }
  // start
  async #restoreCartIfNeeded() {
  const snapshotRaw = sessionStorage.getItem('bundleBuyNowRestoreCart');
  console.log('[bundle-restore] checking sessionStorage:', snapshotRaw);
  if (!snapshotRaw) return;

  sessionStorage.removeItem('bundleBuyNowRestoreCart');

  try {
    const items = JSON.parse(snapshotRaw);
    console.log('[bundle-restore] parsed items:', items);
    if (!Array.isArray(items) || items.length === 0) return;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionIds = new Set();
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionIds.add(el.dataset.sectionId);
      }
    });

    const headerSectionEl = document.querySelector('[id^="shopify-section-"][id*="header"]');
    const headerSectionId = headerSectionEl?.id.replace('shopify-section-', '');
    if (headerSectionId) sectionIds.add(headerSectionId);

    console.log('[bundle-restore] sending restore request, sections:', Array.from(sectionIds));

    const addResponse = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items,
        sections: Array.from(sectionIds).join(','),
      }),
    });

    const addResult = await addResponse.json();
    console.log('[bundle-restore] add response:', addResult);

    if (addResult.status) {
      console.error('[bundle-restore] Failed to restore cart:', addResult.message);
      return;
    }

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    console.log('[bundle-restore] final cart state:', cart);

    document.dispatchEvent(
      new CartUpdateEvent(cart, 'cart-drawer-restore', {
        source: 'cart-drawer-restore',
        itemCount: cart.item_count,
        sections: addResult.sections,
      })
    );
    console.log('[bundle-restore] CartUpdateEvent dispatched successfully');
  } catch (error) {
    console.error('[bundle-restore] Failed to restore cart after Buy Now:', error);
  }
}
// end

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    // start
    window.removeEventListener('pageshow', this.#handlePageShow);
    // end
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    // start
    this.#restoreCartIfNeeded();
    // end
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
